import api from '@/lib/services/general.service'
import { ToFloatTimeString, toAmPmTimeString, toTimeString, getTimeLength } from "@/components/common/vue-calendar/functions/calndr-helper"
import { toast } from 'vue3-toastify';
import { loginForSave } from '@/lib/services/guest'
import { canCreateAsGuest, addGuestDraft } from '@/lib/services/guestDrafts'

const initialState = (important = false) => ({
  events: [],
  requestData: {
    timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
    start: new Date().toISOString(),
    view: 4,
    important,
  },
  isLoading: true,
  dataLoaded: new Set(),
});
export const state = initialState();
export const getters = {
  events: s => s.events,
  importantEvents: (state) => {
    let items = state.events;
    return items
      .filter(e => e.isImportant)
      .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
  },
  important: s => s.requestData.important,
  isLoading: s => s.isLoading,
  dataLoaded: s => s.dataLoaded,

};
export const actions = {
  async fetchEventsAndCounts({ commit, dispatch, state }, newRequestData) {
    const important = state.requestData.important;
    commit('RESET_STATE', important);
    await Promise.all([
      dispatch('fetchEvents', newRequestData),
    ]);
  },

  async fetchEvents({ commit, state }, newRequestData) {
    commit('SET_LOADING', true)
    let requestData = null;
    if (newRequestData === null) {
      requestData = {
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        start: new Date().toISOString(),
        view: 4,
        important: state.requestData.important,
      }
    } else {
      requestData = newRequestData;
    }
    const currentRequestData = state.requestData;
    const dataKey = `${requestData.start}-${requestData.view}-${requestData.important}`;
    if (newRequestData !== null && state.dataLoaded.has(dataKey)) {
      commit('SET_REQUEST_DATA', { data: requestData, dataKey: null });
      return;
    }
    if (newRequestData === null || JSON.stringify(requestData) !== JSON.stringify(currentRequestData)) {
      commit('SET_REQUEST_DATA', { data: requestData, dataKey });
      try {
        await fetchEventsWithRetry(commit, requestData, 0);
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },
  async addEvent({ commit, dispatch, rootGetters }, event) {
    // Guests keep up to GUEST_LIMIT events locally; the next one requires signing in.
    if (!rootGetters['user/isAuthenticated']) {
      if (!canCreateAsGuest('event')) { loginForSave(); return new Promise(() => {}) }
      const payload = { ...event }; delete payload.silentToast
      const id = addGuestDraft('event', payload)
      const now = new Date().toISOString()
      const local = { ...payload, id, _local: true, createdDate: now, updateDate: now }
      commit('ADD_EVENT', [local])
      return local
    }
    const silent = !!event.silentToast
    delete event.silentToast
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'event',
      }
    );
    try {
      let response = await api.insert('event', event);
      if (response) {
        if (event.timelineId) {
          dispatch('timeline/addEventIntoTimeline', response.data, { root: true });
          // dispatch('sidepanel/addEventIntoTimeline', response.data, { root: true });
        }
        commit("ADD_EVENT", response.data.data);
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: 'Event added',
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'event',
            isLoading: false,
          });
          toast.done(toastId);
        });
        // Return the created event (id + timelineId) so callers can attach documents. Truthy, so
        // existing `if (await addEvent(...))` checks keep working.
        return response.data.data;
      }
      return false;
    } catch (error) {
      console.error('Error Adding Event:', error);
      return false;
    }
  },
  async markAsImportant({ commit, dispatch }, request) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'event',
      }
    );
    const count = request.ids.length
    let message = ''

    if (count === 1) {
      message = 'Event sets as important'
    } else {
      message = count + ' events sets as important'
    }

    try {
      const response = await api.insert('event/markAsImportant', request);
      if (response) {
        response.data.forEach(item => {
          if (item.timelineId) {
            dispatch('timeline/editEventTimeline', item, { root: true });
            dispatch('sidepanel/editEventTimeline', item, { root: true });
          }
        });
        commit("UPDATE_EVENTS", response.data);
        setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'event',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking event as important:', error);
      return false;
    }
  },
  async markAsNotImportant({ commit, dispatch }, request) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'event',
      }
    );
    const count = request.ids.length
    let message = ''

    if (count === 1) {
      message = 'Event sets as not important'
    } else {
      message = count + ' events sets as not important'
    }

    try {
      const response = await api.insert('event/markAsNotImportant', request);
      if (response) {
        response.data.forEach(item => {
          if (item.timelineId) {
            dispatch('timeline/editEventTimeline', item, { root: true });
            dispatch('sidepanel/editEventTimeline', item, { root: true });
          }
        });
        commit("UPDATE_EVENTS", response.data);
        setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'event',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error marking event as not important:', error);
      return false;
    }
  },
  async deleteEvent({ commit, dispatch, state }, request) {
    // `request` may be the bare id array it always was, or `{{ ids, silent }}` (2026-09-15 parity
    // audit B-5/C-9, busy-my-web#466/#474): the universal box reports a delete in its own pink strip
    // with Undo, and the global toast that used to fire alongside it was the second notification the
    // audit reports for one action. Silent is the BOX's choice — every list page still toasts.
    const ids = Array.isArray(request) ? request : (request?.ids || [])
    const silent = !Array.isArray(request) && !!request?.silent
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'event',
      }
    );
    const count = ids.length
    let message = ''

    if (count === 1) {
      message = 'Event deleted'
    } else {
      message = count + ' events deleted'
    }

    try {
      const response = await api.deleteWithRequest('event/delete', { ids });
      if (response.data) {
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'event',
            isLoading: false,
          });
          toast.done(toastId);
        });
        commit("REMOVE_EVENTS", ids);
        // Deleting the last entity out of an ongoing empties (hides) it server-side — re-read the
        // ongoing list so the sidebar drops it without a manual refresh. Restoring brings it back.
        dispatch('ongoing/syncAfterEntityChange', null, { root: true });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting Event:', error);
      return false;
    }
  },
  /**
   * Undo for deleteEvent (busy-my-web#163). Mirrors task's `restoreTasks` and note's `restoreNotes`:
   * the SAME rows come back, so nothing that points at an event — timeline entries, deep links, the
   * card the user is still looking at — is orphaned by the round trip.
   *
   * `POST event/restore` (busy-main-bff#14 → clndr-clndr-calendar-srv 42d660e) takes the same
   * `{ ids }` body `event/delete` takes and answers with the restored events, so the ids that come
   * back are the ids that went in. Callers rely on that: see SmartBox's Undo, which asserts it.
   * Restoring an id that is not deleted is a server-side no-op, which makes a double Undo harmless.
   */
  async restoreEvents({ commit, dispatch }, ids) {
    const count = ids.length;
    const message = count === 1 ? 'Event restored' : `${count} events restored`;
    const toastId = toast.loading('Please wait...', { theme: 'event' });
    try {
      const response = await api.insert('event/restore', { ids });
      const restored = response?.data ?? [];
      restored.forEach(item => {
        if (item.timelineId) {
          dispatch('timeline/addEventIntoTimeline', item, { root: true });
        }
      });
      commit('ADD_EVENT', restored);
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'event',
          isLoading: false,
        });
        toast.done(toastId);
      });
      // Restoring the event un-hides the ongoing it was the last member of — the mirror of the
      // sync deleteEvent fires.
      dispatch('ongoing/syncAfterEntityChange', null, { root: true });
      return restored;
    } catch (error) {
      console.error('Error restoring events:', error);
      // Null, not an empty list: the caller has to be able to tell "the restore failed, fall back to
      // the snapshot" apart from "the server restored nothing".
      return null;
    }
  },
  async editEvent({ commit, dispatch }, { id, request }) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'event',
      }
    );
    try {
      const response = await api.patch('event', id, request);
      if (response) {
        if (response.data.timelineId) {
          dispatch('timeline/editEventTimeline', response.data, { root: true });
          dispatch('sidepanel/editEventTimeline', response.data, { root: true });
        }
        commit("UPDATE_EVENTS", response.data);
        setTimeout(() => {
          toast.update(toastId, {
            render: "Event updated",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'event',
            isLoading: false,
          });
          toast.done(toastId);
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error editing Event:', error);
      return false;
    }
  },
  loadImportant({ commit, dispatch, state }, important) {
    if (state.requestData.important !== important) {
      let request = {
        timeZoneId: state.requestData.timeZoneId,
        start: state.requestData.start,
        view: state.requestData.view,
        important: important,
      }
      dispatch('fetchEvents', request);
    }
  }
}
async function fetchEventsWithRetry(commit, requestData, retryCount) {
  try {
    let response = null;
    let requestParams = { ...requestData };
    if (requestParams.important !== true) {
      delete requestParams.important;
    }
    response = await api.get('event', requestParams);
    if (response) {
      commit("SET_EVENTS", response.data.data);
    }
  } catch (error) {
    console.error('Error fetching events:', error);
    if (retryCount < 3) {
      await new Promise(resolve => setTimeout(resolve, 10000));
      await fetchEventsWithRetry(commit, requestData, retryCount + 1);
    } else {
      console.error('Max retries reached. Failed to fetch events.');
    }
  }
}
export const mutations = {
  RESET_STATE(state, important) {
    Object.assign(state, initialState(important));
  },
  SET_LOADING(s, isLoading) {
    s.isLoading = isLoading;
  },
  SET_REQUEST_DATA(s, { data, dataKey }) {
    s.requestData.timeZoneId = data.timeZoneId;
    s.requestData.start = data.start;
    s.requestData.view = data.view;
    s.requestData.important = data.important
    if (dataKey !== null) {
      s.dataLoaded.add(dataKey);
    }
  },
  SET_EVENTS(s, events) {
    console.log(events)
    events.forEach(e => {
      try {
        let stDate = new Date(e.start)
        let enDate = new Date(e.end)
        let dNoTime = new Date(e.start)
        dNoTime.setHours(0, 0, 0, 0)
        const existingEventIndex = s.events.findIndex(existingEvent => existingEvent.id === e.id);
        const eventData = {
          timelineId: e.timelineId,
          id: e.id,
          timelineHeadLine: e.timelineHeadLine,
          summary: e.summary,
          participants: e.participants,
          description: e.description,
          time: toAmPmTimeString(stDate),
          time_long: toTimeString(stDate),
          end_time: toAmPmTimeString(enDate),
          end_time_long: toTimeString(enDate),
          start_time_dec: ToFloatTimeString(stDate),
          length: getTimeLength(stDate, enDate),
          eventType: e.eventType,
          dateNoTime: dNoTime,
          startDateTime: stDate,
          endDateTime: enDate,
          day: dNoTime.getDay(),
          href: "#",
          color: "blue",
          isImportant: e.isImportant,
          isAllDay: e.isAllDay,
          calendarId: e.calendarId ?? null,
          calendarColor: e.calendarColor ?? null,
          scheduleType: e.scheduleType ?? null,
          // The repeat rule, carried through untouched: the listing groups the events that repeat
          // under their own header ("Weekly n") and stamps them with the rule instead of a day
          // (Figma 3793-36498). This mapping is a WHITELIST, so anything not named here is dropped
          // before the listing ever sees it — which is why the rule had to be added by name.
          rules: e.rules ?? e.recurrence ?? null,
          // Who is invited and where it happens (clndr-clndr-calendar-srv#6 — the BFF has passed both
          // through since the 2026-09-15 audit, but this whitelist dropped them): the event reader's
          // guests and location rows (lib/entityReader, 2026-10-02) and the rail's own two rows.
          attendees: Array.isArray(e.attendees) ? e.attendees : [],
          location: e.location ?? null,
          remindDate: e.remindDate ?? null,
        };
        if (existingEventIndex === -1) {
          s.events.push(eventData);
        } else {
          s.events[existingEventIndex] = { ...s.events[existingEventIndex], ...eventData };
        }
      }
      catch (error) {
        console.error(error)
      }
    })
    s.isLoading = false
  },
  UPDATE_EVENTS(s, responses) {
    if (Array.isArray(responses)) {
      responses.forEach(response => {
        const event = s.events.find(event => event.id === response.id);
        if (event) {
          let stDate = new Date(response.startDateTime)
          let enDate = new Date(response.endDateTime)
          let dNoTime = new Date(response.startDateTime)
          dNoTime.setHours(0, 0, 0, 0)
          event.time = toAmPmTimeString(stDate),
            event.time_long = toTimeString(stDate),
            event.end_time = toAmPmTimeString(enDate),
            event.end_time_long = toTimeString(enDate),
            event.start_time_dec = ToFloatTimeString(stDate),
            event.length = getTimeLength(stDate, enDate),
            event.dateNoTime = dNoTime,
            event.startDateTime = stDate,
            event.endDateTime = enDate,
            event.day = dNoTime.getDay(),
            event.isImportant = response.isImportant;
          event.isAllDay = response.isAllDay;
          event.summary = response.summary;
          event.description = response.description;
          event.eventType = response.eventType;
          event.participants = responses.participants;
          event.timelineHeadLine = response.timelineHeadLine;
        }
      });
    } else {
      // Handle case where responses is a single object
      let stDate = new Date(responses.startDateTime)
      let enDate = new Date(responses.endDateTime)
      let dNoTime = new Date(responses.startDateTime)
      dNoTime.setHours(0, 0, 0, 0)
      const event = s.events.find(event => event.id === responses.id);
      if (event) {
        event.time = toAmPmTimeString(stDate),
          event.time_long = toTimeString(stDate),
          event.end_time = toAmPmTimeString(enDate),
          event.end_time_long = toTimeString(enDate),
          event.start_time_dec = ToFloatTimeString(stDate),
          event.length = getTimeLength(stDate, enDate),
          event.dateNoTime = dNoTime,
          event.startDateTime = stDate,
          event.endDateTime = enDate,
          event.day = dNoTime.getDay(),
          event.isImportant = responses.isImportant;
        event.isAllDay = responses.isAllDay;
        event.summary = responses.summary;
        event.description = responses.description;
        event.eventType = responses.eventType;
        event.participants = responses.participants;
      }
    }
  },
  REMOVE_EVENTS(s, ids) {
    s.events = s.events.filter(item => !ids.includes(item.id));
  },
  UPDATE_CALENDAR_COLOR(s, { calendarId, color }) {
    s.events.forEach(e => { if (e.calendarId === calendarId) e.calendarColor = color })
  },
  ADD_EVENT(s, event) {
    event.forEach(e => {
      try {
        let stDate = new Date(e.start)
        let enDate = new Date(e.end)
        let dNoTime = new Date(e.start)
        dNoTime.setHours(0, 0, 0, 0)
        let eventData = {
          id: e.id,
          summary: e.summary,
          timelineHeadline: e.timelineHeadline,
          description: e.description,
          time: toAmPmTimeString(stDate),
          time_long: toTimeString(stDate),
          end_time: toAmPmTimeString(enDate),
          end_time_long: toTimeString(enDate),
          start_time_dec: ToFloatTimeString(stDate),
          length: getTimeLength(stDate, enDate),
          eventType: e.eventType,
          participants: e.participants,
          dateNoTime: dNoTime,
          startDateTime: stDate,
          endDateTime: enDate,
          day: dNoTime.getDay(),
          href: "#",
          colour: "blue",
          isImportant: e.isImportant,
          isAllDay: e.isAllDay,
          calendarId: e.calendarId ?? null,
          calendarColor: e.calendarColor ?? null,
          scheduleType: e.scheduleType ?? null,
        };
        s.events.push(eventData);
      }
      catch (error) {
        console.error(error)
      }
    })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
};